// src/components/TournamentSeedingEditor.js
// Admin component for manually seeding and managing tournament brackets
import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, Alert, Modal, TextInput,
} from 'react-native';
import { db } from '../../firebaseConfig';
import {
  doc, getDoc, getDocs, collection, writeBatch,
  updateDoc, serverTimestamp, query, where, orderBy,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

// ============================================================================
// THEME
// ============================================================================
const COLORS = {
  GOLD: '#FCD000',
  BG: '#0b0b0b',
  SURFACE: '#111111',
  SURFACE2: '#1a1a1a',
  TEXT: '#FFFFFF',
  DIM: '#888888',
  BORDER: '#333333',
  SUCCESS: '#2ECC71',
  DANGER: '#E74C3C',
  BLUE: '#3498DB',
  ORANGE: '#F39C12',
  PURPLE: '#9B59B6',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function TournamentSeedingEditor({ tournamentId, onClose, onSeedingComplete }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tournament, setTournament] = useState(null);
  const [entries, setEntries] = useState([]);
  const [matches, setMatches] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null); // { matchId, slot: 'p1' | 'p2' }

  // ============================================================================
  // DATA LOADING
  // ============================================================================
  const loadTournamentData = useCallback(async () => {
    if (!tournamentId) return;
    
    setLoading(true);
    try {
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      const tournamentSnap = await getDoc(tournamentRef);
      
      if (!tournamentSnap.exists()) {
        Alert.alert('Error', 'Tournament not found');
        onClose?.();
        return;
      }
      
      setTournament({ id: tournamentSnap.id, ...tournamentSnap.data() });
      
      // Load entries
      const entriesSnap = await getDocs(collection(tournamentRef, 'entries'));
      const entriesList = entriesSnap.docs.map(d => ({
        id: d.id,
        odinalId: d.id,
        ...d.data(),
      }));
      setEntries(entriesList);
      
      // Load bracket matches
      const matchesSnap = await getDocs(
        query(collection(tournamentRef, 'matches'), orderBy('round'), orderBy('matchNumber'))
      );
      const matchesList = matchesSnap.docs.map(d => ({
        id: d.id,
        ...d.data(),
      }));
      setMatches(matchesList);
      
    } catch (error) {
      console.error('Error loading tournament:', error);
      Alert.alert('Error', error.message);
    } finally {
      setLoading(false);
    }
  }, [tournamentId, onClose]);

  useEffect(() => {
    loadTournamentData();
  }, [loadTournamentData]);

  // ============================================================================
  // SEEDING ACTIONS
  // ============================================================================
  
  // Create empty bracket structure (for manual seeding)
  const handleCreateEmptyBracket = async () => {
    if (entries.length < 2) {
      Alert.alert('Error', 'Need at least 2 entries to create bracket');
      return;
    }
    
    setSaving(true);
    try {
      const numEntries = entries.length;
      let bracketSize = 1;
      while (bracketSize < numEntries) bracketSize *= 2;
      const numRounds = Math.log2(bracketSize);
      const numFirstRoundMatches = bracketSize / 2;
      
      const batch = writeBatch(db);
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      
      // Delete existing matches
      for (const match of matches) {
        batch.delete(doc(tournamentRef, 'matches', match.id));
      }
      
      const newMatches = [];
      
      // First round matches - ALL EMPTY
      for (let i = 0; i < numFirstRoundMatches; i++) {
        const matchId = `round1_match${i + 1}`;
        const matchRef = doc(tournamentRef, 'matches', matchId);
        
        const matchData = {
          round: 1,
          matchNumber: i + 1,
          status: 'pending',
          p1: null,  // Empty - to be filled manually
          p2: null,  // Empty - to be filled manually
          winnerId: null,
          loserId: null,
          nextMatchId: numRounds > 1 ? `round2_match${Math.floor(i / 2) + 1}` : null,
          nextMatchSlot: i % 2 === 0 ? 'p1' : 'p2',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        
        batch.set(matchRef, matchData);
        newMatches.push({ id: matchId, ...matchData });
      }
      
      // Create subsequent round placeholders
      let matchesInRound = numFirstRoundMatches / 2;
      for (let round = 2; round <= numRounds; round++) {
        for (let i = 0; i < matchesInRound; i++) {
          const matchId = `round${round}_match${i + 1}`;
          const matchRef = doc(tournamentRef, 'matches', matchId);
          const isLastRound = round === numRounds;
          
          const matchData = {
            round,
            matchNumber: i + 1,
            status: 'pending',
            p1: null,
            p2: null,
            winnerId: null,
            loserId: null,
            nextMatchId: isLastRound ? null : `round${round + 1}_match${Math.floor(i / 2) + 1}`,
            nextMatchSlot: i % 2 === 0 ? 'p1' : 'p2',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          
          batch.set(matchRef, matchData);
          newMatches.push({ id: matchId, ...matchData });
        }
        matchesInRound = matchesInRound / 2;
      }
      
      // Update tournament - NOTE: Don't set 'seeded' yet, only when all players assigned
      batch.update(tournamentRef, {
        bracketSize,
        totalRounds: numRounds,
        entryCount: numEntries,
        seeding: 'manual',
        bracketCreatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      
      await batch.commit();
      setMatches(newMatches);
      setTournament(prev => ({ ...prev, bracketSize, totalRounds: numRounds, seeding: 'manual' }));
      
      Alert.alert('Success', `Empty bracket created with ${numFirstRoundMatches} matches. Assign all players, then click "Mark as Seeded".`);
      
    } catch (error) {
      console.error('Error creating empty bracket:', error);
      Alert.alert('Error', error.message);
    } finally {
      setSaving(false);
    }
  };

  // Auto-seed: Randomly assign entries to Round 1 slots
  const handleAutoSeed = async (shuffle = true) => {
    if (entries.length < 2) {
      Alert.alert('Error', 'Need at least 2 entries to seed');
      return;
    }
    
    setSaving(true);
    try {
      const numEntries = entries.length;
      let bracketSize = 1;
      while (bracketSize < numEntries) bracketSize *= 2;
      const numRounds = Math.log2(bracketSize);
      const numFirstRoundMatches = bracketSize / 2;
      
      // Shuffle entries if requested
      const orderedEntries = shuffle 
        ? [...entries].sort(() => Math.random() - 0.5)
        : [...entries].sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0));
      
      const batch = writeBatch(db);
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      
      // Delete existing matches
      for (const match of matches) {
        batch.delete(doc(tournamentRef, 'matches', match.id));
      }
      
      // Create new bracket
      const newMatches = [];
      
      // First round matches
      for (let i = 0; i < numFirstRoundMatches; i++) {
        const p1Entry = orderedEntries[i * 2] || null;
        const p2Entry = orderedEntries[i * 2 + 1] || null;
        
        const matchId = `round1_match${i + 1}`;
        const matchRef = doc(tournamentRef, 'matches', matchId);
        
        const p1 = p1Entry ? {
          userId: p1Entry.id,
          displayName: p1Entry.displayName || p1Entry.userName || 'Player',
          ign: p1Entry.ign || null,
          isBye: false,
        } : { isBye: true };
        
        const p2 = p2Entry ? {
          userId: p2Entry.id,
          displayName: p2Entry.displayName || p2Entry.userName || 'Player',
          ign: p2Entry.ign || null,
          isBye: false,
        } : { isBye: true };
        
        const matchData = {
          round: 1,
          matchNumber: i + 1,
          status: (p1Entry && p2Entry) ? 'pending' : 'completed',
          p1,
          p2,
          winnerId: null,
          loserId: null,
          nextMatchId: numRounds > 1 ? `round2_match${Math.floor(i / 2) + 1}` : null,
          nextMatchSlot: i % 2 === 0 ? 'p1' : 'p2',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        
        // Auto-advance byes
        if (p1Entry && !p2Entry) {
          matchData.winnerId = p1Entry.id;
          matchData.completedReason = 'bye';
        } else if (p2Entry && !p1Entry) {
          matchData.winnerId = p2Entry.id;
          matchData.completedReason = 'bye';
        }
        
        batch.set(matchRef, matchData);
        newMatches.push({ id: matchId, ...matchData });
      }
      
      // Create subsequent round placeholders
      let matchesInRound = numFirstRoundMatches / 2;
      for (let round = 2; round <= numRounds; round++) {
        for (let i = 0; i < matchesInRound; i++) {
          const matchId = `round${round}_match${i + 1}`;
          const matchRef = doc(tournamentRef, 'matches', matchId);
          const isLastRound = round === numRounds;
          
          const matchData = {
            round,
            matchNumber: i + 1,
            status: 'pending',
            p1: null,
            p2: null,
            winnerId: null,
            loserId: null,
            nextMatchId: isLastRound ? null : `round${round + 1}_match${Math.floor(i / 2) + 1}`,
            nextMatchSlot: i % 2 === 0 ? 'p1' : 'p2',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          
          batch.set(matchRef, matchData);
          newMatches.push({ id: matchId, ...matchData });
        }
        matchesInRound = matchesInRound / 2;
      }
      
      // Update tournament status
      batch.update(tournamentRef, {
        status: 'seeded',
        seeding: shuffle ? 'random' : 'ordered',
        seededAt: serverTimestamp(),
        bracketSize,
        totalRounds: numRounds,
        entryCount: numEntries,
        updatedAt: serverTimestamp(),
      });
      
      await batch.commit();
      setMatches(newMatches);
      setTournament(prev => ({ ...prev, status: 'seeded', seeding: shuffle ? 'random' : 'ordered', bracketSize, totalRounds: numRounds }));
      
      Alert.alert('Success', `Bracket created with ${numFirstRoundMatches} first-round matches`);
      onSeedingComplete?.();
      
    } catch (error) {
      console.error('Error auto-seeding:', error);
      Alert.alert('Error', error.message);
    } finally {
      setSaving(false);
    }
  };

  // Manual assignment: Assign selected entry to selected slot
  const handleAssignEntry = async () => {
    if (!selectedEntry || !selectedSlot) {
      Alert.alert('Select Both', 'Click an entry from the list, then click a bracket slot to assign');
      return;
    }
    
    setSaving(true);
    try {
      const { matchId, slot } = selectedSlot;
      const matchRef = doc(db, 'tournaments', tournamentId, 'matches', matchId);
      const matchSnap = await getDoc(matchRef);
      
      if (!matchSnap.exists()) {
        throw new Error('Match not found');
      }
      
      const matchData = matchSnap.data();
      
      // Check if entry is already assigned elsewhere in this round
      const entryInRound = matches.find(m => 
        m.round === matchData.round &&
        (m.p1?.userId === selectedEntry.id || m.p2?.userId === selectedEntry.id)
      );
      
      if (entryInRound && entryInRound.id !== matchId) {
        Alert.alert(
          'Already Assigned',
          `${selectedEntry.ign || selectedEntry.displayName} is already in ${entryInRound.id}. Remove them first?`,
          [
            { text: 'Cancel', style: 'cancel' },
            { 
              text: 'Remove & Reassign', 
              onPress: async () => {
                // Remove from old match
                const oldSlot = entryInRound.p1?.userId === selectedEntry.id ? 'p1' : 'p2';
                await updateDoc(doc(db, 'tournaments', tournamentId, 'matches', entryInRound.id), {
                  [oldSlot]: { isBye: true },
                  updatedAt: serverTimestamp(),
                });
                // Then assign to new slot
                await performAssignment(matchRef, slot, matchData);
              }
            },
          ]
        );
        setSaving(false);
        return;
      }
      
      await performAssignment(matchRef, slot, matchData);
      
    } catch (error) {
      console.error('Error assigning entry:', error);
      Alert.alert('Error', error.message);
    } finally {
      setSaving(false);
    }
  };

  const performAssignment = async (matchRef, slot, matchData) => {
    const playerData = {
      userId: selectedEntry.id,
      displayName: selectedEntry.displayName || selectedEntry.userName || 'Player',
      ign: selectedEntry.ign || null,
      isBye: false,
    };
    
    // Check if both slots will now be filled
    const otherSlot = slot === 'p1' ? 'p2' : 'p1';
    const otherPlayer = matchData[otherSlot];
    const bothFilled = otherPlayer && !otherPlayer.isBye;
    
    await updateDoc(matchRef, {
      [slot]: playerData,
      status: bothFilled ? 'pending' : matchData.status,
      updatedAt: serverTimestamp(),
    });
    
    // Update local state
    setMatches(prev => prev.map(m => 
      m.id === matchRef.id 
        ? { ...m, [slot]: playerData, status: bothFilled ? 'pending' : m.status }
        : m
    ));
    
    setSelectedEntry(null);
    setSelectedSlot(null);
    
    Alert.alert('Assigned', `${selectedEntry.ign || selectedEntry.displayName} assigned to ${slot.toUpperCase()}`);
  };

  // Remove player from slot
  const handleRemoveFromSlot = async (matchId, slot) => {
    setSaving(true);
    try {
      const matchRef = doc(db, 'tournaments', tournamentId, 'matches', matchId);
      
      await updateDoc(matchRef, {
        [slot]: { isBye: true },
        updatedAt: serverTimestamp(),
      });
      
      setMatches(prev => prev.map(m => 
        m.id === matchId ? { ...m, [slot]: { isBye: true } } : m
      ));
      
    } catch (error) {
      Alert.alert('Error', error.message);
    } finally {
      setSaving(false);
    }
  };

  // Force winner on a match
  const handleForceWinner = async (matchId, winnerId, winnerName) => {
    Alert.alert(
      'Force Winner',
      `Set ${winnerName} as winner of this match?`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          style: 'destructive',
          onPress: async () => {
            setSaving(true);
            try {
              const matchRef = doc(db, 'tournaments', tournamentId, 'matches', matchId);
              const matchSnap = await getDoc(matchRef);
              const matchData = matchSnap.data();
              
              const loserId = matchData.p1?.userId === winnerId 
                ? matchData.p2?.userId 
                : matchData.p1?.userId;
              
              await updateDoc(matchRef, {
                status: 'completed',
                winnerId,
                loserId: loserId || null,
                completedAt: serverTimestamp(),
                completedReason: 'admin_forced',
                updatedAt: serverTimestamp(),
              });
              
              // The onTournamentMatchWinner trigger should handle advancement
              await loadTournamentData();
              Alert.alert('Success', 'Winner set. Advancement trigger should fire.');
              
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setSaving(false);
            }
          },
        },
      ]
    );
  };

  // Mark tournament as seeded (locked -> seeded) - for manual seeding workflow
  const handleMarkAsSeeded = async () => {
    // Check if all Round 1 matches have both players assigned
    const round1Matches = matches.filter(m => m.round === 1);
    const incompleteMatches = round1Matches.filter(m => {
      const p1Valid = m.p1 && (m.p1.userId || m.p1.isBye);
      const p2Valid = m.p2 && (m.p2.userId || m.p2.isBye);
      return !p1Valid || !p2Valid;
    });
    
    if (incompleteMatches.length > 0) {
      Alert.alert(
        'Incomplete Bracket',
        `${incompleteMatches.length} match(es) still need players assigned. Please fill all slots before marking as seeded.`,
        [{ text: 'OK' }]
      );
      return;
    }
    
    Alert.alert(
      'Mark as Seeded',
      'All players are assigned. Mark this tournament as SEEDED? It will go LIVE automatically at the scheduled start time.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          onPress: async () => {
            setSaving(true);
            try {
              const tournamentRef = doc(db, 'tournaments', tournamentId);
              await updateDoc(tournamentRef, {
                status: 'seeded',
                seededAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
              
              setTournament(prev => ({ ...prev, status: 'seeded' }));
              Alert.alert('Success', 'Tournament marked as SEEDED. It will go LIVE at the scheduled start time.');
              onSeedingComplete?.();
              
            } catch (error) {
              console.error('Error marking as seeded:', error);
              Alert.alert('Error', error.message);
            } finally {
              setSaving(false);
            }
          },
        },
      ]
    );
  };

  // Start tournament (seeded -> live)
  const handleStartTournament = async () => {
    Alert.alert(
      'Start Tournament',
      'Set tournament to LIVE and start Round 1 matches?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Start',
          onPress: async () => {
            setSaving(true);
            try {
              const batch = writeBatch(db);
              const tournamentRef = doc(db, 'tournaments', tournamentId);
              
              batch.update(tournamentRef, {
                status: 'live',
                startedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              });
              
              // Set Round 1 matches to live
              const round1Matches = matches.filter(m => m.round === 1 && m.status === 'pending');
              for (const match of round1Matches) {
                batch.update(doc(tournamentRef, 'matches', match.id), {
                  status: 'live',
                  updatedAt: serverTimestamp(),
                });
              }
              
              await batch.commit();
              await loadTournamentData();
              Alert.alert('Success', 'Tournament is now LIVE!');
              
            } catch (error) {
              Alert.alert('Error', error.message);
            } finally {
              setSaving(false);
            }
          },
        },
      ]
    );
  };

  // ============================================================================
  // HELPERS
  // ============================================================================
  
  // Get entries not yet assigned to Round 1
  const getUnassignedEntries = () => {
    const assignedIds = new Set();
    matches
      .filter(m => m.round === 1)
      .forEach(m => {
        if (m.p1?.userId) assignedIds.add(m.p1.userId);
        if (m.p2?.userId) assignedIds.add(m.p2.userId);
      });
    return entries.filter(e => !assignedIds.has(e.id));
  };

  const getRoundName = (round, totalRounds) => {
    if (round === totalRounds) return 'Finals';
    if (round === totalRounds - 1) return 'Semi-Finals';
    if (round === totalRounds - 2) return 'Quarter-Finals';
    return `Round ${round}`;
  };

  // ============================================================================
  // RENDER
  // ============================================================================
  
  if (loading) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.GOLD} />
          <Text style={styles.loadingText}>Loading tournament...</Text>
        </View>
      </View>
    );
  }

  const unassignedEntries = getUnassignedEntries();
  const round1Matches = matches.filter(m => m.round === 1);
  const laterRoundMatches = matches.filter(m => m.round > 1);
  const totalRounds = tournament?.totalRounds || Math.max(...matches.map(m => m.round), 1);

  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>Tournament Seeding</Text>
          <Text style={styles.subtitle}>
            {tournament?.name || tournamentId} • Status: {tournament?.status?.toUpperCase()}
          </Text>
        </View>
        <TouchableOpacity style={styles.closeBtn} onPress={onClose}>
          <Text style={styles.closeBtnText}>✕</Text>
        </TouchableOpacity>
      </View>

      {/* Action Buttons */}
      <View style={styles.actionBar}>
        {/* Show Create Empty Bracket when no bracket exists */}
        {matches.length === 0 && (
          <TouchableOpacity 
            style={[styles.actionBtn, styles.actionBtnWarning]}
            onPress={handleCreateEmptyBracket}
            disabled={saving}
          >
            <Text style={styles.actionBtnText}>📝 Create Empty Bracket</Text>
          </TouchableOpacity>
        )}
        
        <TouchableOpacity 
          style={[styles.actionBtn, styles.actionBtnPrimary]}
          onPress={() => handleAutoSeed(true)}
          disabled={saving}
        >
          <Text style={styles.actionBtnText}>🎲 Auto-Seed (Random)</Text>
        </TouchableOpacity>
        
        <TouchableOpacity 
          style={[styles.actionBtn, styles.actionBtnSecondary]}
          onPress={() => handleAutoSeed(false)}
          disabled={saving}
        >
          <Text style={styles.actionBtnText}>📋 Seed by Order</Text>
        </TouchableOpacity>
        
        {tournament?.status === 'seeded' && (
          <TouchableOpacity 
            style={[styles.actionBtn, styles.actionBtnSuccess]}
            onPress={handleStartTournament}
            disabled={saving}
          >
            <Text style={styles.actionBtnText}>🚀 Start Tournament</Text>
          </TouchableOpacity>
        )}
        
        {/* Show Mark as Seeded when bracket exists, locked status, and all entries assigned */}
        {matches.length > 0 && tournament?.status === 'locked' && unassignedEntries.length === 0 && (
          <TouchableOpacity 
            style={[styles.actionBtn, styles.actionBtnSuccess]}
            onPress={handleMarkAsSeeded}
            disabled={saving}
          >
            <Text style={styles.actionBtnText}>✅ Mark as Seeded</Text>
          </TouchableOpacity>
        )}
        
        {selectedEntry && selectedSlot && (
          <TouchableOpacity 
            style={[styles.actionBtn, styles.actionBtnWarning]}
            onPress={handleAssignEntry}
            disabled={saving}
          >
            <Text style={styles.actionBtnText}>✓ Assign Selected</Text>
          </TouchableOpacity>
        )}
      </View>

      {saving && (
        <View style={styles.savingOverlay}>
          <ActivityIndicator color={COLORS.GOLD} />
          <Text style={styles.savingText}>Saving...</Text>
        </View>
      )}

      <ScrollView style={styles.scrollContent}>
        {/* Instructions */}
        <View style={styles.instructions}>
          <Text style={styles.instructionsTitle}>Manual Seeding:</Text>
          <Text style={styles.instructionsText}>
            1. Click "Create Empty Bracket" to generate empty slots{'\n'}
            2. Click an entry from the list, then click a bracket slot{'\n'}
            3. Or use "Auto-Seed" to randomly assign all entries
          </Text>
        </View>

        {/* Two Column Layout */}
        <View style={styles.columns}>
          {/* Left: Unassigned Entries */}
          <View style={styles.columnLeft}>
            <Text style={styles.sectionTitle}>
              Unassigned Entries ({unassignedEntries.length})
            </Text>
            
            {entries.length === 0 ? (
              <Text style={styles.emptyText}>No entries yet</Text>
            ) : unassignedEntries.length === 0 ? (
              <Text style={styles.successText}>✓ All entries assigned!</Text>
            ) : (
              unassignedEntries.map(entry => (
                <TouchableOpacity
                  key={entry.id}
                  style={[
                    styles.entryCard,
                    selectedEntry?.id === entry.id && styles.entryCardSelected,
                  ]}
                  onPress={() => setSelectedEntry(
                    selectedEntry?.id === entry.id ? null : entry
                  )}
                >
                  <Text style={styles.entryIGN}>{entry.ign || 'No IGN'}</Text>
                  <Text style={styles.entryName}>
                    {entry.displayName || entry.userName}
                  </Text>
                  <Text style={styles.entryId}>{entry.id.substring(0, 8)}...</Text>
                  {entry.isTestEntry && (
                    <Text style={styles.testBadge}>TEST</Text>
                  )}
                </TouchableOpacity>
              ))
            )}
            
            {/* All Entries List */}
            <Text style={[styles.sectionTitle, { marginTop: 20 }]}>
              All Entries ({entries.length})
            </Text>
            {entries.map(entry => {
              const assignedTo = matches.find(m => 
                m.round === 1 && 
                (m.p1?.userId === entry.id || m.p2?.userId === entry.id)
              );
              return (
                <View key={entry.id} style={styles.entryListItem}>
                  <Text style={styles.entryListIGN}>{entry.ign || 'No IGN'}</Text>
                  {assignedTo ? (
                    <Text style={styles.assignedBadge}>
                      → {assignedTo.id}
                    </Text>
                  ) : (
                    <Text style={styles.unassignedBadge}>Unassigned</Text>
                  )}
                </View>
              );
            })}
          </View>

          {/* Right: Bracket */}
          <View style={styles.columnRight}>
            <Text style={styles.sectionTitle}>Bracket</Text>
            
            {matches.length === 0 ? (
              <View style={styles.noBracket}>
                <Text style={styles.noBracketText}>
                  No bracket created yet.{'\n\n'}
                  • Click "📝 Create Empty Bracket" for manual seeding{'\n'}
                  • Click "🎲 Auto-Seed" to randomly assign players
                </Text>
              </View>
            ) : (
              <>
                {/* Round 1 */}
                <Text style={styles.roundTitle}>
                  {getRoundName(1, totalRounds)} ({round1Matches.length} matches)
                </Text>
                
                {round1Matches.map(match => (
                  <View key={match.id} style={styles.matchCard}>
                    <View style={styles.matchHeader}>
                      <Text style={styles.matchId}>{match.id}</Text>
                      <Text style={[
                        styles.matchStatus,
                        match.status === 'completed' && styles.statusCompleted,
                        match.status === 'live' && styles.statusLive,
                      ]}>
                        {match.status?.toUpperCase()}
                      </Text>
                    </View>
                    
                    {/* P1 Slot */}
                    <TouchableOpacity
                      style={[
                        styles.playerSlot,
                        selectedSlot?.matchId === match.id && selectedSlot?.slot === 'p1' && styles.slotSelected,
                        match.winnerId === match.p1?.userId && styles.slotWinner,
                      ]}
                      onPress={() => setSelectedSlot(
                        selectedSlot?.matchId === match.id && selectedSlot?.slot === 'p1'
                          ? null
                          : { matchId: match.id, slot: 'p1' }
                      )}
                      onLongPress={() => match.p1?.userId && handleRemoveFromSlot(match.id, 'p1')}
                    >
                      <Text style={styles.slotLabel}>P1:</Text>
                      {match.p1?.isBye ? (
                        <Text style={styles.byeText}>BYE</Text>
                      ) : match.p1?.userId ? (
                        <View style={styles.slotPlayer}>
                          <Text style={styles.slotIGN}>{match.p1.ign || match.p1.displayName}</Text>
                          {match.status !== 'completed' && (
                            <TouchableOpacity
                              style={styles.forceWinBtn}
                              onPress={() => handleForceWinner(match.id, match.p1.userId, match.p1.ign || match.p1.displayName)}
                            >
                              <Text style={styles.forceWinText}>👑</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : (
                        <Text style={styles.emptySlot}>Click to assign</Text>
                      )}
                    </TouchableOpacity>
                    
                    <Text style={styles.vsText}>vs</Text>
                    
                    {/* P2 Slot */}
                    <TouchableOpacity
                      style={[
                        styles.playerSlot,
                        selectedSlot?.matchId === match.id && selectedSlot?.slot === 'p2' && styles.slotSelected,
                        match.winnerId === match.p2?.userId && styles.slotWinner,
                      ]}
                      onPress={() => setSelectedSlot(
                        selectedSlot?.matchId === match.id && selectedSlot?.slot === 'p2'
                          ? null
                          : { matchId: match.id, slot: 'p2' }
                      )}
                      onLongPress={() => match.p2?.userId && handleRemoveFromSlot(match.id, 'p2')}
                    >
                      <Text style={styles.slotLabel}>P2:</Text>
                      {match.p2?.isBye ? (
                        <Text style={styles.byeText}>BYE</Text>
                      ) : match.p2?.userId ? (
                        <View style={styles.slotPlayer}>
                          <Text style={styles.slotIGN}>{match.p2.ign || match.p2.displayName}</Text>
                          {match.status !== 'completed' && (
                            <TouchableOpacity
                              style={styles.forceWinBtn}
                              onPress={() => handleForceWinner(match.id, match.p2.userId, match.p2.ign || match.p2.displayName)}
                            >
                              <Text style={styles.forceWinText}>👑</Text>
                            </TouchableOpacity>
                          )}
                        </View>
                      ) : (
                        <Text style={styles.emptySlot}>Click to assign</Text>
                      )}
                    </TouchableOpacity>
                    
                    {match.winnerId && (
                      <Text style={styles.winnerText}>
                        Winner: {match.p1?.userId === match.winnerId ? match.p1?.ign : match.p2?.ign}
                      </Text>
                    )}
                  </View>
                ))}

                {/* Later Rounds */}
                {laterRoundMatches.length > 0 && (
                  <>
                    <Text style={[styles.roundTitle, { marginTop: 20 }]}>Later Rounds</Text>
                    {laterRoundMatches.map(match => (
                      <View key={match.id} style={[styles.matchCard, styles.matchCardLater]}>
                        <View style={styles.matchHeader}>
                          <Text style={styles.matchId}>
                            {getRoundName(match.round, totalRounds)} - Match {match.matchNumber}
                          </Text>
                          <Text style={styles.matchStatus}>{match.status?.toUpperCase()}</Text>
                        </View>
                        <Text style={styles.laterRoundPlayers}>
                          {match.p1?.ign || match.p1?.displayName || 'TBD'} vs{' '}
                          {match.p2?.ign || match.p2?.displayName || 'TBD'}
                        </Text>
                        {match.winnerId && (
                          <Text style={styles.winnerText}>
                            Winner: {match.p1?.userId === match.winnerId ? match.p1?.ign : match.p2?.ign}
                          </Text>
                        )}
                      </View>
                    ))}
                  </>
                )}
              </>
            )}
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.BG,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: COLORS.DIM,
    marginTop: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.BORDER,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    color: COLORS.GOLD,
  },
  subtitle: {
    fontSize: 14,
    color: COLORS.DIM,
    marginTop: 4,
  },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: COLORS.SURFACE2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeBtnText: {
    color: COLORS.TEXT,
    fontSize: 18,
  },
  actionBar: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 12,
    gap: 8,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.BORDER,
  },
  actionBtn: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    marginRight: 8,
    marginBottom: 8,
  },
  actionBtnPrimary: {
    backgroundColor: COLORS.BLUE,
  },
  actionBtnSecondary: {
    backgroundColor: COLORS.SURFACE2,
    borderWidth: 1,
    borderColor: COLORS.BORDER,
  },
  actionBtnSuccess: {
    backgroundColor: COLORS.SUCCESS,
  },
  actionBtnWarning: {
    backgroundColor: COLORS.ORANGE,
  },
  actionBtnText: {
    color: COLORS.TEXT,
    fontWeight: '600',
    fontSize: 14,
  },
  savingOverlay: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    backgroundColor: COLORS.SURFACE2,
  },
  savingText: {
    color: COLORS.GOLD,
    marginLeft: 8,
  },
  scrollContent: {
    flex: 1,
  },
  instructions: {
    margin: 12,
    padding: 12,
    backgroundColor: COLORS.SURFACE,
    borderRadius: 8,
    borderLeftWidth: 3,
    borderLeftColor: COLORS.GOLD,
  },
  instructionsTitle: {
    color: COLORS.GOLD,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  instructionsText: {
    color: COLORS.DIM,
    fontSize: 13,
    lineHeight: 20,
  },
  columns: {
    flexDirection: 'row',
    padding: 12,
  },
  columnLeft: {
    flex: 1,
    paddingRight: 12,
    borderRightWidth: 1,
    borderRightColor: COLORS.BORDER,
  },
  columnRight: {
    flex: 2,
    paddingLeft: 12,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: COLORS.TEXT,
    marginBottom: 12,
  },
  emptyText: {
    color: COLORS.DIM,
    fontStyle: 'italic',
  },
  successText: {
    color: COLORS.SUCCESS,
    fontWeight: '600',
  },
  entryCard: {
    backgroundColor: COLORS.SURFACE,
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  entryCardSelected: {
    borderColor: COLORS.GOLD,
    backgroundColor: COLORS.SURFACE2,
  },
  entryIGN: {
    color: COLORS.TEXT,
    fontWeight: 'bold',
    fontSize: 16,
  },
  entryName: {
    color: COLORS.DIM,
    fontSize: 13,
  },
  entryId: {
    color: COLORS.DIM,
    fontSize: 11,
    fontFamily: 'monospace',
  },
  testBadge: {
    position: 'absolute',
    top: 8,
    right: 8,
    backgroundColor: COLORS.PURPLE,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
    fontSize: 10,
    color: COLORS.TEXT,
    fontWeight: 'bold',
  },
  entryListItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    borderBottomWidth: 1,
    borderBottomColor: COLORS.BORDER,
  },
  entryListIGN: {
    color: COLORS.TEXT,
    fontSize: 13,
  },
  assignedBadge: {
    color: COLORS.SUCCESS,
    fontSize: 11,
  },
  unassignedBadge: {
    color: COLORS.ORANGE,
    fontSize: 11,
  },
  noBracket: {
    padding: 40,
    alignItems: 'center',
  },
  noBracketText: {
    color: COLORS.DIM,
    textAlign: 'center',
    lineHeight: 24,
  },
  roundTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: COLORS.GOLD,
    marginBottom: 12,
    textTransform: 'uppercase',
  },
  matchCard: {
    backgroundColor: COLORS.SURFACE,
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  matchCardLater: {
    opacity: 0.7,
  },
  matchHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  matchId: {
    color: COLORS.DIM,
    fontSize: 12,
    fontFamily: 'monospace',
  },
  matchStatus: {
    color: COLORS.DIM,
    fontSize: 11,
    fontWeight: 'bold',
  },
  statusCompleted: {
    color: COLORS.SUCCESS,
  },
  statusLive: {
    color: COLORS.DANGER,
  },
  playerSlot: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: COLORS.SURFACE2,
    padding: 10,
    borderRadius: 6,
    borderWidth: 2,
    borderColor: 'transparent',
  },
  slotSelected: {
    borderColor: COLORS.GOLD,
  },
  slotWinner: {
    borderColor: COLORS.SUCCESS,
    backgroundColor: '#1a3d1a',
  },
  slotLabel: {
    color: COLORS.DIM,
    fontSize: 12,
    marginRight: 8,
    width: 30,
  },
  slotPlayer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  slotIGN: {
    color: COLORS.TEXT,
    fontWeight: '600',
  },
  byeText: {
    color: COLORS.ORANGE,
    fontStyle: 'italic',
  },
  emptySlot: {
    color: COLORS.DIM,
    fontStyle: 'italic',
  },
  forceWinBtn: {
    padding: 4,
  },
  forceWinText: {
    fontSize: 16,
  },
  vsText: {
    color: COLORS.DIM,
    textAlign: 'center',
    marginVertical: 4,
    fontSize: 12,
  },
  winnerText: {
    marginTop: 8,
    color: COLORS.SUCCESS,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  laterRoundPlayers: {
    color: COLORS.TEXT,
    textAlign: 'center',
  },
});